import React, { useState } from 'react';
import cx from 'classnames';
import { Button, Container, OverlayTrigger, Popover } from 'react-bootstrap';
import Tooltip from 'components/Tooltip';
import { guid } from 'services/util';
import {
  SegmentFilter,
  SegmentContext,
} from 'pages/storyboards/storyboardSettings/personalizationCampaign/components/AudienceSegment/segmentFilter';
import campaignStyles from 'pages/storyboards/storyboardSettings/personalizationCampaign/components/AudienceSegment/audienceSegment.module.scss';
import { CONTENT_GRID_PERSONALIZATION_FILTER_OPTIONS } from './audienceFilterOptions';
import { DEFAULT_FILTER_ROW } from './commands';
import { nextPersonalizationSetName } from './personalizationMappers';
import { PersonalizationSetNameField } from './PersonalizationSetNameField';
import { PersonalizationSetsPanelSkeleton } from './PersonalizationSetsPanelSkeleton';
import {
  SET_INCOMPLETE_COLLAPSED_TOOLTIP,
  SETS_PANEL_INTRO,
} from './filterSummaryClauses';
import { personalizationSetIsSaveable } from './personalizationFilterUtils';
import styles from './personalization.module.scss';

export const PersonalizationSetsPanel = ({
  sets,
  activeSetId,
  loading = false,
  onActiveSetChange,
  onSetsChange,
  dynamic = false,
}) => {
  const [renameSetId, setRenameSetId] = useState(null);

  if (loading) {
    return <PersonalizationSetsPanelSkeleton />;
  }

  const startRenameForSet = (clientId) => {
    if (activeSetId !== clientId) {
      onActiveSetChange(clientId);
    }
    setRenameSetId(clientId);
  };

  const updateSet = (clientId, patch) => {
    onSetsChange(sets.map((set) => (set.clientId === clientId ? { ...set, ...patch } : set)));
  };

  const updateRow = (setId, rowId, row) => {
    const set = sets.find((s) => s.clientId === setId);
    if (!set) return;
    updateSet(setId, {
      filterRows: set.filterRows.map((r) => (r.id === rowId ? row : r)),
    });
  };

  const addRow = (setId) => {
    const set = sets.find((s) => s.clientId === setId);
    if (!set) return;
    updateSet(setId, { filterRows: [...set.filterRows, DEFAULT_FILTER_ROW()] });
  };

  const deleteRow = (setId, rowId) => {
    const set = sets.find((s) => s.clientId === setId);
    if (!set) return;
    const next = set.filterRows.filter((r) => r.id !== rowId);
    updateSet(setId, {
      filterRows: next.length ? next : [DEFAULT_FILTER_ROW()],
    });
  };

  const deleteSet = (clientId) => {
    const next = sets.filter((s) => s.clientId !== clientId);
    onSetsChange(next);
    if (activeSetId === clientId) {
      onActiveSetChange(next[0]?.clientId);
    }
  };

  const addSet = (event) => {
    event?.stopPropagation?.();
    const order = sets.length;
    const newSet = {
      clientId: guid(),
      name: nextPersonalizationSetName(sets),
      order,
      filterRows: [DEFAULT_FILTER_ROW()],
      layoutMappingIds: [],
      contentMappingIds: [],
    };
    onSetsChange([...sets, newSet]);
    onActiveSetChange(newSet.clientId);
  };

  const toggleSet = (clientId) => {
    onActiveSetChange(activeSetId === clientId ? undefined : clientId);
  };

  const renderSetActionsMenu = (set, isExpanded) => {
    if (!isExpanded) {
      return null;
    }

    const popover = (
      <Popover bsPrefix="popover" className={campaignStyles.campaignSettingsPopover}>
        <Popover.Body className={campaignStyles.campaignSettingsPopoverBody}>
          <div className={campaignStyles.campaignSettingsPopoverContent}>
            <span
              role="button"
              tabIndex={0}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => startRenameForSet(set.clientId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  startRenameForSet(set.clientId);
                }
              }}
            >
              Rename
            </span>
            <span
              role="button"
              tabIndex={0}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => deleteSet(set.clientId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  deleteSet(set.clientId);
                }
              }}
            >
              Delete
            </span>
          </div>
        </Popover.Body>
      </Popover>
    );

    return (
      <OverlayTrigger
        trigger="click"
        rootClose
        rootCloseEvent="mousedown"
        placement="bottom"
        overlay={popover}
      >
        <i
          className={cx('cleverstory-more', styles.setMoreIcon)}
          role="button"
          tabIndex={0}
          aria-label="Set actions"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
            }
          }}
        />
      </OverlayTrigger>
    );
  };

  return (
    <aside className={cx(styles.setsPanel, styles.filterDropdowns)}>
      <div className={styles.setsPanelIntro}>
        <p className={styles.setsPanelIntroText}>{SETS_PANEL_INTRO}</p>
      </div>

      {sets.length === 0 && (
        <p className={styles.setsEmptyHint}>
          No personalization sets yet. Add one to define audience filters and asset picks.
        </p>
      )}

      <SegmentContext.Provider value={CONTENT_GRID_PERSONALIZATION_FILTER_OPTIONS}>
        <div className={styles.setsList}>
          {sets.map((set) => {
            const isExpanded = set.clientId === activeSetId;
            const showIncompleteHint =
              !isExpanded && !personalizationSetIsSaveable(set, dynamic);
            return (
              <div key={set.clientId} className={styles.setCard}>
                <div
                  className={cx(styles.setHeaderRow, {
                    [styles.setHeaderRowExpanded]: isExpanded,
                  })}
                >
                  <div className={campaignStyles.detailTag}>
                    <div
                      className={cx(campaignStyles.actionWrapper, {
                        [campaignStyles.active]: isExpanded,
                      })}
                    >
                      <Button
                        type="button"
                        variant="info"
                        className={campaignStyles.expandButton}
                        onClick={() => toggleSet(set.clientId)}
                        aria-label={isExpanded ? 'Collapse set' : 'Expand set'}
                      >
                        <i className="cleverstory-accordian-drop-down" />
                      </Button>
                    </div>
                  </div>

                  <PersonalizationSetNameField
                    set={set}
                    isExpanded={isExpanded}
                    onExpand={() => onActiveSetChange(set.clientId)}
                    onRename={(name) => updateSet(set.clientId, { name })}
                    renameMode={renameSetId === set.clientId}
                    onRenameModeChange={(active) => {
                      if (!active && renameSetId === set.clientId) {
                        setRenameSetId(null);
                      }
                    }}
                  />

                  <div className={styles.setHeaderActions}>
                    {showIncompleteHint && (
                      <Tooltip placement="top" content={SET_INCOMPLETE_COLLAPSED_TOOLTIP}>
                        <i
                          className={cx('cleverstory-warning', styles.setIncompleteHint)}
                          role="img"
                          aria-label={SET_INCOMPLETE_COLLAPSED_TOOLTIP}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Tooltip>
                    )}
                    {renderSetActionsMenu(set, isExpanded)}
                  </div>
                </div>

                {isExpanded && (
                  <Container
                    fluid
                    className={cx(
                      campaignStyles.contentFilterContainer,
                      styles.setFilters,
                      styles.contentGridSegmentFilters,
                    )}
                  >
                    {set.filterRows.map((row, rowIndex) => (
                      <SegmentFilter
                        key={row.id}
                        index={rowIndex}
                        value={row}
                        inline
                        filterLayout="contentGrid"
                        filters={set.filterRows}
                        canDelete={rowIndex !== 0}
                        onChange={(next) => updateRow(set.clientId, row.id, next)}
                        onDelete={() => deleteRow(set.clientId, row.id)}
                        onInsert={() => addRow(set.clientId)}
                        showAddFilter={rowIndex === set.filterRows.length - 1}
                      />
                    ))}
                  </Container>
                )}
              </div>
            );
          })}

          <div className={styles.setsPanelFooter}>
            <Button
              type="button"
              variant="primary"
              className={styles.newSetPrimaryBtn}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={addSet}
            >
              <i className={cx(styles.newSetIcon, 'cleverstory-plus')} />
              New Personalization
            </Button>
          </div>
        </div>
      </SegmentContext.Provider>
    </aside>
  );
};
